import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, RotateCcw, Check } from "lucide-react";
import { PROCESS_STEPS, totalDocs, verdictFor } from "@/data/process";
import { GoldButton } from "@/components/ds";
import { CorridorMap } from "@/components/ds/CorridorMap";
import { Reveal, Eyebrow } from "@/components/site/Reveal";

const key = (s, d) => `${s}:${d}`;

const Process = () => {
  const [open, setOpen] = useState("introduction");
  const [checked, setChecked] = useState(() => new Set());

  useEffect(() => { document.title = "The Pathway — Zorem Commodities LLC"; }, []);

  const toggleDoc = (k) =>
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });

  const pct = Math.round((checked.size / totalDocs) * 100);
  const verdict = verdictFor(pct);

  return (
    <div data-testid="process-page" className="bg-[#0A0A0B] min-h-screen">
      <div className="pt-36 pb-16 bg-[#121215] hairline-b relative overflow-hidden">
        <div className="absolute inset-y-0 right-0 w-1/2 hidden lg:block">
          <img src="/assets/mining-truck.webp" alt="Active mining site with heavy haul trucks at dusk" className="w-full h-full object-cover object-center opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#121215] via-[#121215]/60 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 max-w-3xl lg:max-w-7xl">
          <div className="max-w-2xl">
            <Eyebrow>How a Trade Clears</Eyebrow>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-wide text-white leading-tight">
              Five steps. Every one <span className="gold-text">on the record.</span>
            </h1>
            <p className="mt-6 text-base text-zinc-400 leading-relaxed">
              From first introduction to an ongoing allocation program, every Zorem
              transaction moves through the same five gates — each documented against
              the gold export checklist below. Expand a step, check the documents you
              already hold, and see how close you are to a trial lift.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-3 gap-12 items-start">
        <div className="lg:col-span-2 space-y-4" data-testid="process-timeline">
          {PROCESS_STEPS.map((s, i) => {
            const isOpen = open === s.id;
            const done = s.docs.filter((d) => checked.has(key(s.id, d.id))).length;
            return (
              <Reveal key={s.id} delay={i * 0.06}>
                <div className={`card-line transition-colors ${isOpen ? "bg-[#121215]" : "bg-[#0A0A0B] hover:bg-[#121215]/60"}`}>
                  <button
                    type="button"
                    data-testid={`step-toggle-${s.id}`}
                    onClick={() => setOpen(isOpen ? null : s.id)}
                    aria-expanded={isOpen}
                    className="w-full flex items-center gap-5 px-6 sm:px-8 py-6 text-left"
                  >
                    <span className={`font-mono2 text-sm ${isOpen ? "text-[#E8C85A]" : "text-zinc-400"}`}>{s.n}</span>
                    <span className="flex-1">
                      <span className={`font-display text-lg sm:text-xl tracking-wide block ${isOpen ? "text-white" : "text-zinc-300"}`}>{s.title}</span>
                      <span className="text-[12px] uppercase tracking-[0.18em] text-zinc-400">{s.tagline}</span>
                    </span>
                    <span className="font-mono2 text-[11px] text-[#C9A227] tabular-nums hidden sm:block">{done}/{s.docs.length}</span>
                    <ChevronDown size={18} className={`text-[#C9A227] transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 sm:px-8 pb-8 pt-2 border-t border-[#C9A227]/10">
                          <p className="text-sm text-zinc-400 leading-relaxed max-w-2xl">{s.body}</p>
                          <div className="mt-6 space-y-2.5">
                            {s.docs.map((d) => {
                              const k = key(s.id, d.id);
                              const on = checked.has(k);
                              return (
                                <button
                                  key={d.id}
                                  type="button"
                                  data-testid={`doc-check-${s.id}-${d.id}`}
                                  onClick={() => toggleDoc(k)}
                                  aria-pressed={on}
                                  className={`w-full flex items-center gap-4 px-4 py-3 text-left transition-colors ${on ? "bg-[#1C1C22]" : "bg-[#0A0A0B] hover:bg-[#16161A]"}`}
                                >
                                  <span className={`w-5 h-5 shrink-0 card-line flex items-center justify-center transition-colors ${on ? "bg-[#C9A227] border-[#C9A227]" : ""}`}>
                                    {on && <Check size={13} className="text-[#0A0A0B]" strokeWidth={3} />}
                                  </span>
                                  <span className={`text-sm ${on ? "text-[#E8C85A]" : "text-zinc-400"}`}>{d.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div className="lg:sticky lg:top-32">
          <Reveal>
            <div data-testid="readiness-simulator" className="card-line bg-[#121215] p-8">
              <span className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#C9A227]">Export Readiness</span>
              <div className="mt-5 font-display text-5xl text-white tabular-nums" data-testid="readiness-score">{pct}%</div>
              <div className="mt-4 h-1.5 bg-[#1C1C22] overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-[#997A19] to-[#D4AF37]"
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                />
              </div>
              <p className={`mt-4 font-mono2 text-[11px] uppercase tracking-[0.2em] ${verdict.tone}`} data-testid="readiness-verdict">
                {verdict.label}
              </p>
              <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                {checked.size} of {totalDocs} checklist documents in hand. This simulator is
                indicative only — formal clearance happens through our due diligence process.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <GoldButton to="/mandate" testid="simulator-cta">Submit a Mandate</GoldButton>
                <button
                  type="button"
                  data-testid="simulator-reset"
                  onClick={() => setChecked(new Set())}
                  className="inline-flex items-center gap-2 px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-zinc-400 card-line hover:text-[#D4AF37] hover:bg-[#1C1C22] transition-colors"
                >
                  <RotateCcw size={13} /> Reset
                </button>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <img src="/assets/refined-1kg.webp" alt="Refined 1000g Au 999.9 bar held by a gloved inspector" loading="lazy" className="mt-6 w-full card-line object-cover" />
          </Reveal>
        </div>
      </div>

      <div className="hairline-t">
        <img src="/assets/excavator-banner.webp" alt="CAT 6015 mining excavator on a desert site, monochrome" loading="lazy" className="w-full h-48 sm:h-64 object-cover" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <Reveal>
          <Eyebrow>Corridors</Eyebrow>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-wide text-white leading-snug max-w-2xl">
            Mine gate to vault, <span className="gold-text">across three continents.</span>
          </h2>
          <p className="mt-6 text-base text-zinc-400 leading-relaxed max-w-2xl">
            Supply originates at verified mine gates in the DRC, Rwanda, and West Africa,
            transits through Dubai under documented custody, and settles into recognized
            vaults and refineries in London, Zurich, New York, Miami, and Hong Kong.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="mt-12">
          <CorridorMap />
        </Reveal>
        <div className="mt-8 grid sm:grid-cols-3 gap-px bg-[#C9A227]/15 card-line" data-testid="process-gallery">
          <img src="/assets/mine-pit.webp" alt="Alluvial mining pit with pumps and workers" loading="lazy" className="w-full aspect-[4/3] object-cover" />
          <img src="/assets/mine-aerial.webp" alt="Aerial view of a mining camp in green hills" loading="lazy" className="w-full aspect-[4/3] object-cover" />
          <img src="/assets/assay-bench.webp" alt="Assay laboratory bench with sorted gold samples in pans and jars" loading="lazy" className="w-full aspect-[4/3] object-cover" />
        </div>
      </div>
    </div>
  );
};

export default Process;
